import loadable from '@react-loadable/revised';
import SvgFingerprintRight from 'assets/pricing/fingerprint-right.inline.svg';
import Footer from 'components/Footer';
import Layout from 'components/Layout';
import SelectTextField from 'components/material-ui/SelectTextField';
import { PRICING_DURATION_UNIT, PRICING_PLAN } from 'components/pages/pricing/base/enumsPricing';
import HelperBox from 'components/pages/pricing/items/HelperBox';
import EnterpriseView from 'components/pages/pricing/view/EnterpriseView';
import PlansContainer from 'components/pages/pricing/view/PlansContainer';
import SeoLazy from 'components/SeoLazy';
import { useFormik } from 'formik';
import { Trans, useTranslation } from 'gatsby-plugin-react-i18next';
import useAllPlanSetups from 'hooks/useAllPlanSetups';
import useCommonData from 'hooks/useCommonData';
import useLanguage from 'hooks/useLanguage';
import { QUERY } from 'interfaces/graph';
import { actionsPersist, usePersistDispatch, usePersistState } from 'providers/PersistProvider';
import React, { useEffect } from 'react';
import useInView from 'react-cool-inview';
import { isMobileScreen } from 'utils/helper';

// eslint-disable-next-line react/display-name
export const handleTranslateMonthsSelect = language => (durationUnit, duration) => {
	if (language === 'pl') {
		if (durationUnit === PRICING_DURATION_UNIT.MONTHS) {
			switch (duration) {
				//PL cy - 5,6,7,8,9,12,13,14,15,16,17,18,19,20,21,25,26,27,28,29,30,31
				//PL ce - 2,3,4,22,23,24,32,33,34,42,43,44,52,53,54,62,63,64,72,73,74,82,83,84,92,93,94
				case 1:
					return <Trans i18nKey="pagePricing.options.months.single" values={{ duration }}/>;
				case 6:
					return <Trans i18nKey="pagePricing.options.months.pilotage" values={{ duration }}/>;
				case 12:
					return <Trans i18nKey="pagePricing.options.months.cy" values={{ duration }}/>;
				case 13:
					return <Trans i18nKey="pagePricing.options.months.cy" values={{ duration }}/>;
				case 14:
					return <Trans i18nKey="pagePricing.options.months.cy" values={{ duration }}/>;
				default:
					if (String(duration).charAt(String(duration).length - 1) === '2' ||
						String(duration).charAt(String(duration).length - 1) === '3' ||
						String(duration).charAt(String(duration).length - 1) === '4'
					) {
						return <Trans i18nKey="pagePricing.options.months.ce" values={{ duration }}/>;
					}
					return <Trans i18nKey="pagePricing.options.months.cy" values={{ duration }}/>;
			}
		} else {
			switch (duration) {
				//PL ta - 5,6,7,8,9,12,13,14,15,16,17,18,19,20,21,25,26,27,28,29,30,31
				//PL t - 2,3,4,22,23,24,32,33,34,42,43,44,52,53,54,62,63,64,72,73,74,82,83,84,92,93,94
				case 1:
					return <Trans i18nKey="pagePricing.options.years.single" values={{ duration }}/>;
				case 12:
					return <Trans i18nKey="pagePricing.options.years.t" values={{ duration }}/>;
				case 13:
					return <Trans i18nKey="pagePricing.options.years.t" values={{ duration }}/>;
				case 14:
					return <Trans i18nKey="pagePricing.options.years.t" values={{ duration }}/>;
				default:
					if (String(duration).charAt(String(duration).length - 1) === '2' ||
						String(duration).charAt(String(duration).length - 1) === '3' ||
						String(duration).charAt(String(duration).length - 1) === '4'
					) {
						return <Trans i18nKey="pagePricing.options.years.ta" values={{ duration }}/>;
					}
					return <Trans i18nKey="pagePricing.options.years.t" values={{ duration }}/>;
			}
		}
	} else {
		// ENG
		if (durationUnit === PRICING_DURATION_UNIT.MONTHS) {
			switch (duration) {
				case 1:
					return <Trans i18nKey="pagePricing.options.months.single" values={{ duration }}/>;
				case 6:
					return <Trans i18nKey="pagePricing.options.months.pilotage" values={{ duration }}/>;
				default:
					return <Trans i18nKey="pagePricing.options.months.multiple" values={{ duration }}/>;
			}
		} else {
			switch (duration) {
				case 1:
					return <Trans i18nKey="pagePricing.options.years.single" values={{ duration }}/>;
				default:
					return <Trans i18nKey="pagePricing.options.years.multiple" values={{ duration }}/>;
			}
		}
	}
};

const LazyLoadIndex = loadable({
	loader: () => import('components/pages/pricing/LazyLoadIndex'),
	loading: () => <div/>
});

export default function PricingPage() {
	const { observe, unobserve, inView } = useInView({});
	const dispatchPersist = usePersistDispatch();
	const { t } = useTranslation();
	const { option } = usePersistState();
	const { language } = useLanguage();
	const { loading, allPlanSetups } = useAllPlanSetups();
	const { allPlanDurations } = useCommonData(QUERY.ALL_PLAN_DURATIONS);

	const optionAgreement = option ? option : (allPlanDurations ? allPlanDurations[0] : null);

	const formPricing = useFormik({
		initialValues: {
			months: optionAgreement?.id
		},
		enableReinitialize: true
	});

	useEffect(() => {
		if (inView) {
			unobserve();
		}
	}, [inView]);

	const planEnterprise = allPlanSetups?.find(item => item.name === PRICING_PLAN.ENTERPRISE);
	const isMobile = isMobileScreen();

	return (
		<Layout loading={loading}>
			<div className="relative border-t border-grey-E mt-24">
				<div>
					<div className="sm:text-center mx-15 sm:mx-0">
						<h1 className="mb-14 mt-20 sm:mt-40 text-17 leading-25 sm:text-24">
							{t('pagePricing.plan.h1')}<span className="text-green-1 font-normal text-shadow-none">_</span>
						</h1>
						<div className="sm:flex items-center justify-center">
							<p className="text-12 mr-30 sm:mr-10 mb-10 sm:mb-0">{t('pagePricing.plan.desc')}</p>
							<SelectTextField
								tw="w-110"
								options={allPlanDurations}
								onChange={event => {
									const id = event.target.value;
									dispatchPersist({ type: actionsPersist.updateOption, payload: allPlanDurations.find(item => item.id === id) });
								}}
								form={formPricing}
								variant="outlined"
								name="months"
								labelName="durationUnit"
								labelSecondName="duration"
								labelFunc={handleTranslateMonthsSelect(language)}
							/>
						</div>
					</div>

					<div className="hidden sm:block sm:absolute top-0 right-0 -z-10">
						<SvgFingerprintRight tw="fill-current text-grey-F"/>
					</div>

					<PlansContainer isMobile={isMobile}/>

					{/* Helper */}
					{!isMobile && <HelperBox/>}

					<div ref={observe}>
						<EnterpriseView plan={planEnterprise}/>
					</div>

					<SeoLazy
						isOpen={!inView}
						items={[
							{ id: 1, tag: 'h2', text: t('pagePricing.security.h2') },
							{ id: 2, tag: 'p', text: t('pagePricing.security.rodo.title') },
							{ id: 3, tag: 'p', text: t('pagePricing.security.rodo.desc') },
							{ id: 4, tag: 'p', text: t('pagePricing.security.2FA.title') },
							{ id: 5, tag: 'p', text: t('pagePricing.security.2FA.desc') },
							{ id: 6, tag: 'p', text: t('pagePricing.security.CIS.title') },
							{ id: 7, tag: 'p', text: t('pagePricing.security.CIS.desc') },
							{ id: 8, tag: 'p', text: t('pagePricing.security.CIS.footer.1') },
							{ id: 9, tag: 'p', text: t('pagePricing.security.CIS.footer.2') },
							{ id: 10, tag: 'h2', text: t('pagePricing.weight.h2') },
							{ id: 11, tag: 'h2', text: t('pagePricing.partners.h2') },
							{ id: 12, tag: 'h2', text: t('pagePricing.education.h2') },
							{ id: 13, tag: 'p', text: t('pagePricing.education.desc.1') },
							{ id: 14, tag: 'p', text: t('pagePricing.education.desc.2') },
							{ id: 15, tag: 'p', text: t('pagePricing.education.btnAsk') },
							{ id: 16, tag: 'h2', text: t('pagePricing.question.h2') }
						]}
					/>

					{inView && <LazyLoadIndex isMobile={isMobile}/>}
					<Footer/>
				</div>
			</div>
		</Layout>
	);
}
